/**
 * The browser side of the live stream.
 *
 * The frame types come from `@nouma/worker/protocol` rather than being restated here. That module is
 * deliberately free of any `cloudflare:workers` import, so the contract can be shared without dragging
 * the edge runtime into a browser bundle — and a contract defined once cannot drift into two different
 * ideas of what a Tick frame is.
 *
 * Two decisions in here are worth stating, because both are the opposite of the server's:
 *
 *   - **An unknown server frame is ignored and reported, not treated as fatal.** The server may have
 *     gained a frame type that this bundle predates; a viewer that disconnects over that would be
 *     broken by a deploy it has nothing to do with. The worker refuses unknown *client* frames, since
 *     a client has no business inventing any — the asymmetry is deliberate.
 *
 *   - **Reconnect delay is jittered.** Without it every viewer that lost the socket reconnects on the
 *     same schedule, and the first Tick after an outage reaches a server that is already saturated by
 *     exactly the traffic the outage created.
 */

import type { ServerFrame, TickFrame } from "@nouma/worker/protocol";
import type { Snapshot } from "@nouma/worker/read-models";

/**
 * The slice of `WebSocket` this client uses.
 *
 * Narrower than the DOM type so a test can supply a plain object. A client that could only be tested
 * against a real socket is a client whose reconnect logic never gets tested, and reconnect logic is
 * the part that runs when something has already gone wrong.
 */
export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: "open" | "close" | "error" | "message", listener: (event: never) => void): void;
}

export type ConnectionState = "idle" | "connecting" | "open" | "reconnecting" | "stopped";

export interface RealtimeHandlers {
  readonly onSnapshot?: (snapshot: Snapshot) => void;
  readonly onTick?: (tick: TickFrame) => void;
  readonly onPong?: (at: number) => void;
  /** A malformed or refused frame. Reported, never fatal. */
  readonly onProblem?: (problem: { code: string; message: string }) => void;
  readonly onState?: (state: ConnectionState) => void;
}

export interface RealtimeOptions {
  readonly url: string;
  readonly socketFactory?: (url: string) => SocketLike;
  /** Injected so a test does not wait out the backoff. */
  readonly setTimeoutFn?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  readonly clearTimeoutFn?: (handle: ReturnType<typeof setTimeout>) => void;
  readonly baseDelayMs?: number;
  readonly maxDelayMs?: number;
  /** Returns 0..1. Injected so the jitter is deterministic under test. */
  readonly random?: () => number;
}

export type ParseResult =
  | { readonly ok: true; readonly frame: ServerFrame }
  | { readonly ok: false; readonly kind: "malformed"; readonly code: string; readonly message: string }
  /** A frame this bundle does not know. Not an error — see the note at the top of the file. */
  | { readonly ok: false; readonly kind: "unknown"; readonly type: string };

const KNOWN_SERVER_FRAMES = ["snapshot", "tick", "pong", "error"] as const;

/** Parses one inbound frame. Pure, so every refusal path is testable. */
export function parseServerFrame(raw: string): ParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, kind: "malformed", code: "bad_json", message: "frame is not JSON" };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, kind: "malformed", code: "bad_frame", message: "frame is not an object" };
  }

  const frame = parsed as { type?: unknown; data?: unknown };
  if (typeof frame.type !== "string") {
    return { ok: false, kind: "malformed", code: "no_type", message: "frame has no type" };
  }

  if (!(KNOWN_SERVER_FRAMES as readonly string[]).includes(frame.type)) {
    return { ok: false, kind: "unknown", type: frame.type };
  }

  if (typeof frame.data !== "object" || frame.data === null) {
    return { ok: false, kind: "malformed", code: "no_data", message: "frame has no data" };
  }

  return { ok: true, frame: frame as unknown as ServerFrame };
}

/**
 * The delay before reconnect attempt `attempt` (1-based).
 *
 * Full jitter: half the exponential backoff is fixed and half is random. A purely exponential schedule
 * is only correct for one client; for many, synchronised retries are indistinguishable from a
 * self-inflicted denial of service.
 */
export function reconnectDelay(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  random: () => number,
): number {
  const exponential = Math.min(maxDelayMs, baseDelayMs * 2 ** Math.max(0, attempt - 1));
  return Math.round(exponential * (0.5 + 0.5 * clamp01(random())));
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 15_000;

export class RealtimeClient {
  private readonly options: RealtimeOptions;
  private readonly handlers: RealtimeHandlers;
  private readonly socketFactory: (url: string) => SocketLike;
  private readonly setTimeoutFn: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  private readonly clearTimeoutFn: (handle: ReturnType<typeof setTimeout>) => void;
  private readonly random: () => number;

  private socket: SocketLike | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private current: ConnectionState = "idle";
  /** Named apart from the `attempts` getter: a field and an accessor cannot share a name. */
  private failures = 0;

  constructor(options: RealtimeOptions, handlers: RealtimeHandlers = {}) {
    this.options = options;
    this.handlers = handlers;
    this.socketFactory = options.socketFactory ?? ((url) => new WebSocket(url) as unknown as SocketLike);
    this.setTimeoutFn = options.setTimeoutFn ?? ((handler, ms) => setTimeout(handler, ms));
    this.clearTimeoutFn = options.clearTimeoutFn ?? ((handle) => clearTimeout(handle));
    this.random = options.random ?? Math.random;
  }

  get state(): ConnectionState {
    return this.current;
  }

  /** Reconnect attempts since the last successful open. Zero while healthy. */
  get attempts(): number {
    return this.failures;
  }

  connect(): void {
    if (this.current === "open" || this.current === "connecting" || this.current === "stopped") return;
    this.openSocket();
  }

  /** Asks the server for the current state without waiting for the next Tick. */
  requestSnapshot(): void {
    this.sendFrame({ type: "snapshot" });
  }

  ping(): void {
    this.sendFrame({ type: "ping" });
  }

  /** Closes and stops reconnecting. Idempotent. */
  stop(): void {
    this.setState("stopped");
    if (this.timer !== null) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      try {
        socket.close(1_000, "client stopped");
      } catch {
        // Already gone.
      }
    }
  }

  private setState(next: ConnectionState): void {
    if (this.current === next) return;
    this.current = next;
    this.handlers.onState?.(next);
  }

  private openSocket(): void {
    this.setState("connecting");

    let socket: SocketLike;
    try {
      socket = this.socketFactory(this.options.url);
    } catch {
      // A factory that throws is a broken URL, not a network event. Reconnecting forever would spin.
      this.setState("stopped");
      return;
    }
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.socket !== socket) return; // A superseded socket must not resurrect the state.
      this.failures = 0;
      this.setState("open");
    });

    socket.addEventListener("message", (event: never) => {
      if (this.socket !== socket) return;
      this.receive((event as unknown as { data: unknown }).data);
    });

    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.scheduleReconnect();
    });

    socket.addEventListener("error", () => {
      // A close event follows an error in every implementation worth supporting, so reconnection is
      // scheduled there and only there — acting on both would schedule it twice.
    });
  }

  private receive(data: unknown): void {
    if (typeof data !== "string") {
      this.handlers.onProblem?.({ code: "binary_unsupported", message: "expected a text frame" });
      return;
    }

    const parsed = parseServerFrame(data);

    if (!parsed.ok) {
      if (parsed.kind === "unknown") {
        // Reported but not fatal: the server is ahead of this bundle.
        this.handlers.onProblem?.({
          code: "unknown_frame",
          message: `ignoring unknown server frame "${parsed.type}"`,
        });
      } else {
        this.handlers.onProblem?.({ code: parsed.code, message: parsed.message });
      }
      return;
    }

    switch (parsed.frame.type) {
      case "snapshot":
        this.handlers.onSnapshot?.(parsed.frame.data);
        break;
      case "tick":
        this.handlers.onTick?.(parsed.frame.data);
        break;
      case "pong":
        this.handlers.onPong?.(parsed.frame.data.at);
        break;
      case "error":
        this.handlers.onProblem?.(parsed.frame.data);
        break;
    }
  }

  private scheduleReconnect(): void {
    if (this.current === "stopped") return;

    this.failures += 1;
    const delay = reconnectDelay(
      this.failures,
      this.options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS,
      this.options.maxDelayMs ?? DEFAULT_MAX_DELAY_MS,
      this.random,
    );

    this.setState("reconnecting");
    this.timer = this.setTimeoutFn(() => {
      this.timer = null;
      if (this.current === "stopped") return;
      this.openSocket();
    }, delay);
  }

  private sendFrame(frame: { type: "ping" | "snapshot" }): void {
    const socket = this.socket;
    // Dropped rather than queued when there is no socket. A queued ping delivered after a reconnect
    // measures the wrong round trip, and a queued snapshot request arrives with a snapshot already in
    // flight.
    if (!socket || this.current !== "open") return;
    try {
      socket.send(JSON.stringify(frame));
    } catch {
      // The socket died between the check and the send; the close handler will schedule the retry.
    }
  }
}
